import { useState, useEffect, useRef } from "react";
import { uploadFile } from "../../../../../../requests/funcitons";
import "./ImagePicker.css";

export default function ImagePicker({ value, onChange, style }) {
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState("url"); // "url" ou "file"
    const [urlInput, setUrlInput] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (typeof value === "string" && value) {
            setPreviewUrl(value);
            setUrlInput(value);
        }
    }, [value]);

    const handleUrlChange = (e) => {
        setUrlInput(e.target.value);
        setError(null);
    };

    const handleUrlApply = () => {
        if (!urlInput.trim()) {
            setError("Veuillez entrer une URL");
            return;
        }
        setPreviewUrl(urlInput);
        onChange(urlInput);
        setIsOpen(false);
    };

    const handleFileSelect = async (file) => {
        if (!file) return;
        
        if (!file.type.startsWith("image/")) {
            setError("Le fichier doit être une image");
            return;
        }

        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            setError("L'image ne doit pas dépasser 10MB");
            return;
        }

        setError(null);
        setIsUploading(true);
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);

        try {
            const url = await uploadFile(file);
            setPreviewUrl(url);
            setUrlInput(url);
            onChange(url);
            setIsOpen(false);
        } catch {
            setPreviewUrl(typeof value === "string" ? value : null);
            setError("Échec de l'upload de l'image");
        } finally {
            URL.revokeObjectURL(objectUrl);
            setIsUploading(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleClear = () => {
        setPreviewUrl(null);
        setUrlInput("");
        onChange(null);
        setIsOpen(false);
    };

    const getDisplayValue = () => {
        if (typeof value === "string" && value) {
            return value.length > 25 ? value.substring(0, 25) + "..." : value;
        }
        return "Aucune image";
    };

    return (
        <div className="image-picker-container">
            <button
                type="button"
                className="image-picker-trigger"
                onClick={() => setIsOpen(true)}
                style={style}
            >
                <span className="image-picker-preview">
                    {previewUrl ? (
                        <img src={previewUrl} alt="Preview" />
                    ) : (
                        <span className="image-picker-placeholder">🖼️</span>
                    )}
                </span>
                <span className="image-picker-value">{getDisplayValue()}</span>
            </button>

            {isOpen && (
                <>
                    <div
                        className="image-picker-overlay"
                        onClick={() => setIsOpen(false)}
                    />
                    <div className="image-picker-modal">
                        <div className="image-picker-header">
                            <h3 className="image-picker-title">🖼️ Sélecteur d'image</h3>
                            <button
                                type="button"
                                className="image-picker-close"
                                onClick={() => setIsOpen(false)}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="image-picker-tabs">
                            <button
                                type="button"
                                className={`image-picker-tab ${mode === "url" ? "active" : ""}`}
                                onClick={() => setMode("url")}
                            >
                                <span className="tab-icon">🔗</span>
                                URL
                            </button>
                            <button
                                type="button"
                                className={`image-picker-tab ${mode === "file" ? "active" : ""}`}
                                onClick={() => setMode("file")}
                            >
                                <span className="tab-icon">📁</span>
                                Fichier
                            </button>
                        </div>

                        <div className="image-picker-content">
                            <div className="image-picker-preview-large">
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Prévisualisation" />
                                ) : (
                                    <div className="image-picker-no-preview">
                                        <span className="no-preview-icon">📷</span>
                                        <span className="no-preview-text">Aucune image sélectionnée</span>
                                    </div>
                                )}
                            </div>

                            {mode === "url" && (
                                <div className="image-picker-section">
                                    <label className="image-picker-label">URL de l'image</label>
                                    <div className="image-picker-url-input">
                                        <input
                                            type="text"
                                            value={urlInput}
                                            onChange={handleUrlChange}
                                            placeholder="https://exemple.com/image.jpg"
                                            className="image-picker-input-text"
                                        />
                                        <button
                                            type="button"
                                            className="image-picker-preview-btn"
                                            onClick={() => setPreviewUrl(urlInput)}
                                            title="Prévisualiser"
                                        >
                                            👁️
                                        </button>
                                    </div>
                                </div>
                            )}

                            {mode === "file" && (
                                <div className="image-picker-section">
                                    <label className="image-picker-label">Importer une image</label>
                                    <div
                                        className={`image-picker-dropzone ${isDragging ? "dragging" : ""} ${isUploading ? "uploading" : ""}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        onClick={() => !isUploading && fileInputRef.current?.click()}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFileSelect(e.target.files[0])}
                                            style={{ display: "none" }}
                                        />
                                        <div className="dropzone-content">
                                            <span className="dropzone-icon">
                                                {isDragging ? "📥" : "☁️"}
                                            </span>
                                            <span className="dropzone-text">
                                                {isUploading
                                                    ? "Upload en cours..."
                                                    : isDragging
                                                    ? "Déposez l'image ici"
                                                    : "Glissez-déposez une image ou cliquez pour parcourir"}
                                            </span>
                                            <span className="dropzone-hint">
                                                PNG, JPG, GIF, WebP • Max 10MB
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="image-picker-error">
                                    <span>⚠️</span> {error}
                                </div>
                            )}
                        </div>

                        <div className="image-picker-footer">
                            {previewUrl && (
                                <button
                                    type="button"
                                    className="image-picker-btn image-picker-btn-clear"
                                    onClick={handleClear}
                                >
                                    🗑️ Supprimer
                                </button>
                            )}
                            <div className="image-picker-footer-right">
                                <button
                                    type="button"
                                    className="image-picker-btn image-picker-btn-cancel"
                                    onClick={() => setIsOpen(false)}
                                >
                                    Annuler
                                </button>
                                {mode === "url" && (
                                    <button
                                        type="button"
                                        className="image-picker-btn image-picker-btn-apply"
                                        onClick={handleUrlApply}
                                    >
                                        Appliquer
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
